import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import {
    Rights, ReviewStatuses, signalLabel, signal, severityLabel
} from '@aiinterview/shared';
import { api, ApiError } from '../api/client';
import { useSession } from '../api/session';
import {
    Button, Card, Field, TextArea, StatusPill, Loading, ErrorNote, EmptyState,
    confirmAction, toast
} from '../components/ui';
import { Avatar } from '../components/Avatar';

interface Answer {
    id: number;
    sequence: number;
    criterionName: string | null;
    questionText: string;
    transcriptText: string | null;
    translatedTranscript: string | null;
    durationSeconds: number;
    wordCount: number;
    wordsPerMinute: number;
    fillerCount: number;
    longPauseCount: number;
    aiScore: number | null;
    aiFeedback: string | null;
    noAnswerGiven: boolean;
}

interface ProctorEvent {
    id: number;
    offsetMs: number;
    eventType: string;
    severity: number;
    source: string;
    detail: string | null;
    durationMs: number;
    hasSnapshot: boolean;
    serverFaceCount: number | null;
    serverVerified: boolean | null;
    dominantEmotion: string | null;
    emotionConfidence: number | null;
}

interface Breakdown {
    eventType: string;
    occurrences: number;
    deductionPerOccurrence: number;
    rawDeduction: number;
    cappedDeduction: number;
    categoryCap: number;
    wasCapped: boolean;
}

interface ReportData {
    id: number;
    status: string;
    reviewStatus: string;
    reviewRemarks: string | null;
    reviewedAt: string | null;
    reviewedByName: string | null;
    language: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    consentAcceptedAt: string | null;
    durationSeconds: number | null;
    profileName: string;
    integrityScore: number | null;
    integrityVerdict: string | null;
    integrityBreakdown: Breakdown[];
    integrityTotalDeduction: number;
    flaggedCount: number;
    timelineSpanMs: number;
    aiOverallScore: number | null;
    aiSummary: string | null;
    aiScoringSkipped: boolean;
    processingError: string | null;
    candidate: {
        id: number; fullName: string; email: string | null; mobile: string | null;
        externalRef: string | null; position: string | null;
    };
    template: { name: string; position: string | null; maxScorePerAnswer: number; passMark: number };
    answers: Answer[];
    events: ProctorEvent[];
    recording: { available: boolean; permitted: boolean; missing: boolean };
}

/** Bounded, so a stuck interview does not reload for ever. */
const MAX_PROCESSING_POLLS = 20;

const formatOffset = (ms: number): string => {
    const total = Math.round(ms / 1000);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return '—';
    const minutes = Math.floor(seconds / 60);
    return minutes > 0 ? `${minutes}m ${seconds % 60}s` : `${seconds}s`;
};

export function Report() {
    const { id } = useParams();
    const { can } = useSession();
    const queryClient = useQueryClient();
    const [polls, setPolls] = useState(0);
    const [remarks, setRemarks] = useState('');
    const [error, setError] = useState<ApiError | null>(null);
    const [openSnapshot, setOpenSnapshot] = useState<number | null>(null);

    const { data, isLoading } = useQuery({
        queryKey: ['report', id],
        queryFn: async () => {
            const report = await api.get<ReportData>(`/api/admin/interviews/${id}`);
            if (report.status === 'Processing') setPolls((p) => p + 1);
            return report;
        },
        // Still processing means the report is filling in behind the candidate. Polling stops after a
        // bounded number of tries rather than spinning for ever on a genuinely stuck session.
        refetchInterval: (query) =>
            query.state.data?.status === 'Processing' && polls < MAX_PROCESSING_POLLS ? 3000 : false
    });

    const review = useMutation({
        mutationFn: (decision: string) =>
            api.post(`/api/admin/interviews/${id}/review`, { decision, remarks }),
        onSuccess: async () => {
            toast('Decision recorded.');
            await queryClient.invalidateQueries({ queryKey: ['report', id] });
            await queryClient.invalidateQueries({ queryKey: ['interviews'] });
        },
        onError: (caught: ApiError) => setError(caught)
    });

    if (isLoading) return <Loading what="Loading the report" />;
    if (!data) return <ErrorNote message="That interview could not be loaded." />;

    const decided = data.reviewStatus !== ReviewStatuses.Pending;
    const consentTime = data.consentAcceptedAt
        ? new Date(data.consentAcceptedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
        : '—';

    return (
        <div className="space-y-6">
            <div className="grid gap-6 xl:grid-cols-[1fr_22rem]">
                <Card>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <Avatar name={data.candidate.fullName} />
                            <div>
                                {/* The candidate is the subject of this page, so their name is its
                                    heading. It used to borrow the card-title class and override the
                                    size inline, which is what a missing heading level looks like. */}
                                <h1 className="app-page-heading">{data.candidate.fullName}</h1>
                                <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                    {data.candidate.position ?? data.template.position}
                                    {data.candidate.externalRef && (
                                        <span className="app-mono ml-2">{data.candidate.externalRef}</span>
                                    )}
                                </p>
                                <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                    {data.template.name} · camera rules “{data.profileName}”
                                    {data.language && ` · answered in ${data.language}`}
                                </p>
                            </div>
                        </div>

                        <StatusPill kind="status" value={data.status} />
                    </div>

                    <div className="report-stats">
                        <div>
                            <div className="report-stat-label">Duration</div>
                            <div className="report-stat-value">{formatDuration(data.durationSeconds)}</div>
                        </div>
                        <div>
                            <div className="report-stat-label">Answered</div>
                            <div className="report-stat-value">{data.answers.length}</div>
                        </div>
                        <div>
                            <div className="report-stat-label">Flagged events</div>
                            <div className="report-stat-value">{data.flaggedCount}</div>
                        </div>
                        <div>
                            <div className="report-stat-label">Consent</div>
                            <div className="report-stat-value">{consentTime}</div>
                        </div>
                    </div>

                    {data.status === 'Processing' && (
                        <p className="mt-4 rounded-lg px-3 py-2 text-sm" style={{ background: '#e0f2fe', color: '#075985' }}>
                            {polls < MAX_PROCESSING_POLLS
                                ? 'Still being processed. This page will update itself.'
                                : 'This is taking longer than expected. Reload to check again — the '
                                  + 'transcript below is already complete.'}
                        </p>
                    )}

                    {/* A failed stage is stated rather than hidden: "not scored" beats a zero, and a
                        blank panel looks like a bug. */}
                    {data.processingError && (
                        <p className="mt-4 rounded-lg px-3 py-2 text-sm" style={{ background: '#fef3c7', color: '#92400e' }}>
                            {data.processingError}
                        </p>
                    )}

                    {/* Written by the scorer, so it is absent exactly when scoring was skipped or
                        failed - which the notice above has already said. It reads here rather than
                        in a card of its own below the proctoring table: it is the first thing worth
                        knowing, and it was the last thing anybody scrolled to. */}
                    {data.aiSummary && (
                        <div
                            className="mt-4 border-t pt-4"
                            style={{ borderColor: 'var(--app-border-soft)' }}
                        >
                            <div className="report-stat-label">Summary</div>
                            <p className="mt-1 whitespace-pre-wrap text-sm">{data.aiSummary}</p>
                        </div>
                    )}
                </Card>

                <Card>
                    <div className="text-center">
                        {data.integrityScore === null ? (
                            <EmptyState message="Not scored yet." />
                        ) : (
                            <>
                                <div className="report-score">
                                    {data.integrityScore}<span className="report-score-of">/100</span>
                                </div>
                                <div className="mt-2">
                                    <StatusPill kind="verdict" value={data.integrityVerdict} />
                                </div>
                                <p className="mt-3 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                    Integrity score. Advisory only — it never decides an outcome.
                                </p>
                            </>
                        )}

                        <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--app-border-soft)' }}>
                            {data.aiScoringSkipped || data.aiOverallScore === null ? (
                                <>
                                    <div className="text-sm font-medium" style={{ color: 'var(--app-heading)' }}>
                                        Not scored
                                    </div>
                                    <p className="mt-1 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                        No AI provider was configured when this interview finished. The
                                        transcripts below are complete.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <div className="report-score" style={{ fontSize: '2rem' }}>
                                        {data.aiOverallScore}
                                        <span className="report-score-of"> / {data.template.maxScorePerAnswer}</span>
                                    </div>
                                    <p className="mt-2 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                        Overall AI score · pass mark {data.template.passMark}
                                    </p>
                                </>
                            )}
                        </div>
                    </div>
                </Card>
            </div>

            {/* The whole point of keeping the breakdown: a score nobody can account for is a score
                nobody can defend in a hiring conversation. */}
            {data.integrityBreakdown.length > 0 && (
                <Card
                    title="Why points were deducted"
                    lede="Computed from the camera rules this interview actually ran under, so it stays reproducible even after those rules are retuned."
                >
                    <div className="overflow-x-auto">
                        <table className="app-table">
                            <thead>
                                <tr>
                                    <th>Signal</th>
                                    <th className="text-right">Times</th>
                                    <th className="text-right">Each</th>
                                    <th className="text-right">Raw</th>
                                    <th className="text-right">Applied</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.integrityBreakdown.map((row) => (
                                    <tr key={row.eventType}>
                                        <td>{signalLabel(row.eventType)}</td>
                                        <td className="text-right">{row.occurrences}</td>
                                        <td className="text-right">−{row.deductionPerOccurrence}</td>
                                        <td className="text-right">−{row.rawDeduction}</td>
                                        <td className="text-right font-semibold">
                                            −{row.cappedDeduction}
                                            {/* Saying the cap applied is the difference between a
                                                number that looks wrong and one that is explained. */}
                                            {row.wasCapped && (
                                                <span
                                                    className="ml-1 text-xs font-normal"
                                                    style={{ color: 'var(--text-muted-safe)' }}
                                                    title={`Capped at ${row.categoryCap} for this signal.`}
                                                >
                                                    capped
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={4} className="text-right">Total deducted</td>
                                    <td className="text-right">−{data.integrityTotalDeduction}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </Card>
            )}

            <Card
                title="Session timeline"
                lede={data.events.length > 0 ? 'Click a marker to see the frame captured at that moment.' : undefined}
            >
                {data.events.length === 0 ? (
                    <EmptyState message="Nothing was recorded during this interview." />
                ) : (
                    <>
                        <div className="timeline-track">
                            {data.events.map((event) => (
                                <button
                                    key={event.id}
                                    type="button"
                                    className={`timeline-marker sev-${event.severity}`}
                                    style={{
                                        left: `${Math.min(100, Math.max(0, (event.offsetMs * 100) / data.timelineSpanMs))}%`
                                    }}
                                    title={`${formatOffset(event.offsetMs)} · ${signalLabel(event.eventType)}`}
                                    onClick={() => setOpenSnapshot(event.hasSnapshot ? event.id : null)}
                                />
                            ))}
                        </div>
                        <div className="timeline-ends">
                            <span>00:00</span>
                            <span>{formatOffset(data.timelineSpanMs)}</span>
                        </div>

                        {openSnapshot !== null && (
                            <div className="mt-3">
                                <img
                                    src={`/api/admin/interviews/${data.id}/snapshot/${openSnapshot}`}
                                    alt="Frame captured at this moment"
                                    className="max-w-sm rounded-lg border"
                                    style={{ borderColor: 'var(--app-border)' }}
                                />
                                <Button
                                    variant="secondary"
                                    className="mt-2"
                                    onClick={() => setOpenSnapshot(null)}
                                >
                                    Close
                                </Button>
                            </div>
                        )}

                        <div className="mt-4 overflow-x-auto">
                            <table className="app-table">
                                <thead>
                                    <tr>
                                        <th>At</th>
                                        <th>Event</th>
                                        <th>Detail</th>
                                        <th>Server check</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.events.map((event) => (
                                        <tr key={event.id}>
                                            <td className="app-mono">{formatOffset(event.offsetMs)}</td>
                                            <td>
                                                <span className={`severity-chip sev-${event.severity}`} aria-hidden="true" />
                                                {/* Always the plain-English label. A raw stored code on
                                                    a report is the bug plainLanguage exists to prevent. */}
                                                <span style={{ color: 'var(--app-heading)' }}>
                                                    {signalLabel(event.eventType)}
                                                </span>
                                                <span className="ml-2 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                                    · {severityLabel(event.severity)}
                                                </span>
                                                <span className="block text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                                    {signal(event.eventType)?.explanation}
                                                </span>
                                            </td>
                                            <td>
                                                {event.detail ?? '—'}
                                                {event.durationMs > 0 && (
                                                    <span className="block text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                                        held for {Math.round(event.durationMs / 1000)}s
                                                    </span>
                                                )}
                                                {event.dominantEmotion && (
                                                    <span className="block text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                                        looked {event.dominantEmotion}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {event.serverFaceCount === null
                                                    ? '—'
                                                    : `— (${event.serverFaceCount} face${event.serverFaceCount === 1 ? '' : 's'})`}
                                                {event.serverVerified === false && (
                                                    <span className="block text-xs font-medium" style={{ color: '#b91c1c' }}>
                                                        did not match the browser's report
                                                    </span>
                                                )}
                                                {event.source === 'Server' && (
                                                    <span className="block text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                                        found by the server
                                                    </span>
                                                )}
                                            </td>
                                            <td className="text-right">
                                                {event.hasSnapshot && (
                                                    <Button
                                                        variant="secondary"
                                                        onClick={() => setOpenSnapshot(event.id)}
                                                    >
                                                        <i className="bi bi-image" aria-hidden="true" />
                                                        View photo
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </Card>

            <Card title="Answers">
                {data.answers.length === 0 ? (
                    <EmptyState message="No answers were recorded." />
                ) : (
                    <div className="space-y-5">
                        {data.answers.map((answer) => (
                            <article
                                key={answer.id}
                                className="rounded-lg border p-4"
                                style={{ borderColor: 'var(--app-border)' }}
                            >
                                <header className="flex flex-wrap items-start justify-between gap-3">
                                    <div>
                                        <span className="text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                            Question {answer.sequence}
                                            {answer.criterionName && ` · ${answer.criterionName}`}
                                        </span>
                                        <h3 className="mt-0.5 text-sm font-medium" style={{ color: 'var(--app-heading)' }}>
                                            {answer.questionText}
                                        </h3>
                                    </div>

                                    {answer.aiScore !== null && (
                                        <span
                                            className="shrink-0 rounded-lg px-2.5 py-1 text-sm font-semibold"
                                            style={{ background: 'var(--app-bg)', color: 'var(--app-heading)' }}
                                        >
                                            {answer.aiScore} / {data.template.maxScorePerAnswer}
                                        </span>
                                    )}
                                </header>

                                <div
                                    className="mt-3 rounded-lg p-3 text-sm"
                                    style={{ background: 'var(--app-bg)' }}
                                >
                                    {answer.noAnswerGiven ? (
                                        <span style={{ color: 'var(--text-muted-safe)' }}>
                                            Nothing recognisable was said.
                                        </span>
                                    ) : (
                                        // Falls back to the original when there is no translation,
                                        // which is every English answer and every older interview.
                                        <p className="whitespace-pre-wrap">
                                            {answer.translatedTranscript ?? answer.transcriptText}
                                        </p>
                                    )}
                                </div>

                                {answer.translatedTranscript && answer.transcriptText && (
                                    <details className="mt-2">
                                        <summary className="cursor-pointer text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                            Show what the candidate actually said
                                        </summary>
                                        <p
                                            className="mt-2 whitespace-pre-wrap rounded-lg p-3 text-sm"
                                            style={{ background: 'var(--app-bg)' }}
                                        >
                                            {answer.transcriptText}
                                        </p>
                                    </details>
                                )}

                                {answer.aiFeedback && (
                                    <p className="mt-3 text-sm">{answer.aiFeedback}</p>
                                )}

                                <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                                    <span>{answer.durationSeconds} s</span>
                                    <span>{answer.wordCount} words</span>
                                    <span>{answer.wordsPerMinute} wpm</span>
                                    <span>{answer.fillerCount} fillers</span>
                                    <span>{answer.longPauseCount} long pauses</span>
                                </dl>
                            </article>
                        ))}
                    </div>
                )}
            </Card>

            <Card title="Session recording">
                {/* Three distinct states: allowed and present, allowed and absent, not allowed. An
                    empty player for any of them would be a lie. */}
                {!data.recording.permitted ? (
                    <p className="text-sm">You do not have permission to watch recordings.</p>
                ) : data.recording.missing ? (
                    <p className="text-sm">No recording is stored for this interview.</p>
                ) : (
                    <>
                        <video
                            controls
                            preload="metadata"
                            src={`/api/admin/interviews/${data.id}/recording`}
                            className="w-full max-w-2xl rounded-lg bg-black"
                        />
                        <p className="mt-2 text-xs" style={{ color: 'var(--text-muted-safe)' }}>
                            Every view of a recording is written to the audit log.
                        </p>
                    </>
                )}
            </Card>

            {can(Rights.Review) && (
                <Card title="Review">
                    {error && <ErrorNote message={error.message} />}

                    {decided ? (
                        <>
                            <div className="flex items-center gap-3">
                                <StatusPill kind="review" value={data.reviewStatus} />
                                <span className="text-sm" style={{ color: 'var(--text-muted-safe)' }}>
                                    {data.reviewedByName
                                        ? `Decided by ${data.reviewedByName}`
                                        : 'Already decided'}
                                    {data.reviewedAt && ` on ${new Date(data.reviewedAt).toLocaleString()}`}
                                    . This cannot be changed.
                                </span>
                            </div>
                            {data.reviewRemarks && <p className="mt-3 text-sm">{data.reviewRemarks}</p>}
                        </>
                    ) : data.status !== 'Completed' ? (
                        <p className="text-sm">
                            This interview is not finished yet, so there is nothing to decide.
                        </p>
                    ) : (
                        <div className="max-w-xl space-y-4">
                            <Field label="Remarks" hint="Kept with the decision.">
                                <TextArea
                                    value={remarks}
                                    onChange={(e) => setRemarks(e.target.value)}
                                    rows={3}
                                    placeholder="Required when rejecting."
                                />
                            </Field>

                            <div className="flex gap-2">
                                {/* busy disables both, which is what stops the double-submit the server
                                    also refuses with a 409. */}
                                <Button
                                    className="app-btn-approve"
                                    busy={review.isPending}
                                    onClick={async () => {
                                        const confirmed = await confirmAction({
                                            title: `Approve ${data.candidate.fullName}?`,
                                            confirmText: 'Approve'
                                        });
                                        if (confirmed) review.mutate(ReviewStatuses.Approved);
                                    }}
                                >
                                    <i className="bi bi-check-circle" aria-hidden="true" />
                                    Approve
                                </Button>
                                <Button
                                    variant="danger-quiet"
                                    busy={review.isPending}
                                    onClick={async () => {
                                        if (!remarks.trim()) {
                                            toast('Remarks are required when rejecting.', 'error');
                                            return;
                                        }
                                        const confirmed = await confirmAction({
                                            title: `Reject ${data.candidate.fullName}?`,
                                            confirmText: 'Reject',
                                            danger: true
                                        });
                                        if (confirmed) review.mutate(ReviewStatuses.Rejected);
                                    }}
                                >
                                    <i className="bi bi-x-circle" aria-hidden="true" />
                                    Reject
                                </Button>
                            </div>
                        </div>
                    )}
                </Card>
            )}

            <Link to="/interviews" className="inline-block text-sm hover:underline" style={{ color: 'var(--brand-primary)' }}>
                Back to interviews
            </Link>
        </div>
    );
}
